import { useAgentComplianceReviewIgnoreAndResubmitMutation } from '@client/graphql/__generated__/main-operations';
import { AgentComplianceReviewIgnoreAndResubmitInput } from '@client/graphql/__generated__/types';
import { useCallback } from 'react';

interface UseAgentComplianceReviewIgnoreAndResubmitArgs {
  /** Share-link token (`?token=` query param). */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * The per-call portion of the resubmit input — the token + folder are bound by
 * the hook, so callers supply the document being resubmitted and the agent's
 * note (`message`) posted to the reviewer.
 */
export type IgnoreAndResubmitArgs = Omit<
  AgentComplianceReviewIgnoreAndResubmitInput,
  'token' | 'ingestionFolderUuid'
>;

interface UseAgentComplianceReviewIgnoreAndResubmitResult {
  loading: boolean;
}

/**
 * Wraps the token-bound `agentComplianceReviewIgnoreAndResubmit` mutation for
 * the agent compliance review page. Binds the share-link token + folder uuid so
 * callers only pass the document + the agent's resubmit note (`message`).
 *
 * After a successful resubmit it refetches the overview + the open document
 * detail (awaiting the refetch) so the backend-derived `agentResubmittedAt` /
 * counts drive the UI instead of a local optimistic guess.
 *
 * @returns `[ignoreAndResubmit, { loading }]` mirroring the apollo mutation tuple.
 */
export const useAgentComplianceReviewIgnoreAndResubmit = ({
  token,
  ingestionFolderUuid,
}: UseAgentComplianceReviewIgnoreAndResubmitArgs): [
  (args: IgnoreAndResubmitArgs) => Promise<void>,
  UseAgentComplianceReviewIgnoreAndResubmitResult,
] => {
  const [mutate, { loading }] =
    useAgentComplianceReviewIgnoreAndResubmitMutation({
      refetchQueries: [
        'AgentComplianceReviewByShareLinkToken',
        'AgentComplianceReviewDocument',
      ],
      awaitRefetchQueries: true,
    });

  const ignoreAndResubmit = useCallback(
    async (args: IgnoreAndResubmitArgs): Promise<void> => {
      // Never fire the write with empty-string fallback identifiers — a missing
      // token/folder is a not-ready state, not a valid request to send.
      if (
        token == null ||
        token === '' ||
        ingestionFolderUuid == null ||
        ingestionFolderUuid === ''
      ) {
        return;
      }
      await mutate({
        variables: {
          input: {
            token,
            ingestionFolderUuid,
            ...args,
          },
        },
      });
    },
    [mutate, token, ingestionFolderUuid]
  );

  return [ignoreAndResubmit, { loading }];
};
